/**
 * Environment configuration utilities
 * Centralizes all environment variable access
 */

/**
 * Vite-compatible environment configuration
 * Uses import.meta.env instead of process.env
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  devApiUrl: string | undefined;
  prodApiUrl: string | undefined;
  environment: string;
  isProduction: boolean;
  isDevelopment: boolean;
}

/**
 * Get current environment configuration using Vite's import.meta.env
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  // Use consistent REACT_APP_ prefix (since VITE_ variables need explicit setup)
  const isProduction = import.meta.env.REACT_APP_ENV === 'production';

  return {
    apiBaseUrl: getApiBaseUrl(),
    devApiUrl: import.meta.env.REACT_APP_DEV_API_URL,
    prodApiUrl: import.meta.env.REACT_APP_PROD_API_URL,
    environment: import.meta.env.REACT_APP_ENV || import.meta.env.MODE || 'development',
    isProduction,
    isDevelopment: !isProduction
  };
};

/**
 * Unified environment detection for main-branch-only strategy
 */
export const getApiBaseUrl = (): string => {
  console.log('🔍 Unified Environment Debug:', {
    REACT_APP_API_BASE_URL: import.meta.env.REACT_APP_API_BASE_URL,
    REACT_APP_ENV: import.meta.env.REACT_APP_ENV,
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server'
  });

  // Priority 1: Explicit API base URL
  if (import.meta.env.REACT_APP_API_BASE_URL) {
    console.log('✅ Using explicit REACT_APP_API_BASE_URL:', import.meta.env.REACT_APP_API_BASE_URL);
    return import.meta.env.REACT_APP_API_BASE_URL;
  }

  // Priority 2: Environment-based detection
  const isExplicitProd = import.meta.env.REACT_APP_ENV === 'production';
  const isViteProd = import.meta.env.PROD; // Vite's built-in production flag
  
  // Priority 3: Domain-based detection (fallback)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProdDomain = hostname === 'opsai.chat' || 
                      hostname === 'app.opsai.chat' || 
                      hostname === 'www.opsai.chat';
  
  const isProduction = isExplicitProd || (isViteProd && isProdDomain);
  
  const apiUrl = isProduction ? 'https://api.opsai.chat' : 'https://dev.opsai.chat';
  
  console.log('🔄 Environment Detection Result:', {
    isProduction,
    isExplicitProd,
    isViteProd, 
    isProdDomain,
    selectedApiUrl: apiUrl
  });
  
  return apiUrl;
};



/**
 * Get Java Orchestrator WebSocket URL
 */
export const getWebSocketUrl = (): string => {
  const apiBaseUrl = getApiBaseUrl();
  const isProduction = import.meta.env.PROD;
  
  // For Java orchestrator WebSocket (through gateway, same domain as API)
  const wsUrl = apiBaseUrl.replace(/^https?:\/\//, isProduction ? 'wss://' : 'ws://');
  console.log('🔄 Java Orchestrator WebSocket URL (through gateway):', wsUrl);
  return wsUrl;
};

/**
 * Environment variable validation
 */
export const validateEnvironment = (): void => {
  const config = getEnvironmentConfig();
  
  console.log('🔧 Environment Configuration:', {
    environment: config.environment,
    isProduction: config.isProduction,
    apiBaseUrl: config.apiBaseUrl,
    devApiUrl: config.devApiUrl,
    prodApiUrl: config.prodApiUrl
  });
  
  // Validate required environment variables
  if (!config.apiBaseUrl) {
    throw new Error('API base URL is not configured');
  }
  
  // Fixed: Use import.meta.env instead of process.env
  if (config.isProduction && !config.prodApiUrl && !import.meta.env.REACT_APP_API_BASE_URL) {
    console.warn('⚠️ Production API URL not configured, using fallback');
  }
  
  if (config.isDevelopment && !config.devApiUrl && !import.meta.env.REACT_APP_API_BASE_URL) {
    console.warn('⚠️ Development API URL not configured, using fallback');
  }
};

// Validate environment on import (in development)
// Fixed: Use import.meta.env instead of process.env
if (import.meta.env.DEV) {
  try {
    validateEnvironment();
  } catch (error) {
    console.error('❌ Environment validation failed:', error);
  }
}